import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { loginWithPassword } from './loginService';
import { installBfcacheReload } from '../lib/api/bfcache';
import { dismissBootScreen } from '../components/shell/bootScreen';
import { APP_VERSION } from '../version';

export function LoginPage({ authenticate = loginWithPassword, onSuccess = () => window.location.assign('/') }: {
    authenticate?: (username: string, password: string) => Promise<void>;
    onSuccess?: () => void;
}): ReactNode {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const usernameRef = useRef<HTMLInputElement>(null);
    const passwordRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
        /* The login shell's splash has nothing to wait for beyond this mount:
           the card it hands off to is now on screen. Dismissed here rather
           than in login/main.tsx so the handoff is asserted by this
           component's own tests. No-ops when the splash is absent. */
        dismissBootScreen();
        if (!usernameRef.current?.value) usernameRef.current?.focus();
        return installBfcacheReload();
    }, []);
    const submit = async (event: FormEvent): Promise<void> => {
        event.preventDefault(); setError('');
        const user = username.trim(); const secret = password.trim();
        if (!user || !secret) { setError('Please enter your username and password.'); return; }
        setLoading(true);
        try { await authenticate(user, secret); onSuccess(); }
        catch (caught) {
            setError(caught instanceof Error ? caught.message : 'An unexpected error occurred. Please try again.');
            setPassword(''); passwordRef.current?.focus(); setLoading(false);
        }
    };
    return (
        <div className="login-card ui-card">
            <div className="login-logo"><img src="/static/icons/icon-white-transparent-192.png" alt="" /></div>
            <div className="login-title">Spider</div><div className="login-sub ui-text-muted">Sign in to access your databases</div>
            <form onSubmit={(event) => { void submit(event); }}>
                <div className="field-group"><label className="field-label ui-label-field" htmlFor="username">Username</label><div className="field-wrap"><i className="ti ti-user field-icon" aria-hidden="true" /><input ref={usernameRef} type="text" id="username" className="ui-input" placeholder="enter username" autoComplete="username" value={username} onChange={(event) => setUsername(event.target.value)} disabled={loading} /></div></div>
                <div className="field-group"><label className="field-label ui-label-field" htmlFor="password">Password</label><div className="field-wrap"><i className="ti ti-lock field-icon" aria-hidden="true" /><input ref={passwordRef} type="password" id="password" className="ui-input" placeholder="enter password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} disabled={loading} /></div></div>
                <button className="btn-signin ui-button ui-button--primary ui-button--block" id="btn-login" type="submit" disabled={loading}><i className={loading ? 'ti ti-loader-2 ui-spin' : 'ti ti-login'} aria-hidden="true" /> {loading ? 'Signing in…' : 'Sign In'}</button>
            </form>
            <div id="login-error" className="ui-text-error" role={error ? 'alert' : undefined}>{error}</div>
            <div className="login-footer ui-text-muted">{APP_VERSION}</div>
        </div>
    );
}
